import { z } from 'zod';
import { createCursorPaginationSchema } from '@mb/common';

/**
 * Shared Person Validation Schemas
 * Used by both server (REST handlers) and client (form validation)
 */

// Common validation rules (can be reused)
const personBaseSchema = z.object({
  title: z.string().max(10).optional(),
  firstName: z.string().max(100).min(1, 'First name is required'),
  lastName: z.string().max(100).min(1, 'Last name is required'),
  email: z.string().email('Invalid email format').max(255),
  phone: z.string().max(30).optional(),
  privateMobile: z.string().max(30).optional(),
  street: z.string().max(255).optional(),
  postalCode: z.string().max(20).optional(),
  city: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  socialSecurityNumber: z.string().max(50).optional(),
  personnelNumber: z.string().max(50).optional(),
  vacationDays: z
    .union([z.string(), z.number()])
    .optional()
    .transform((value) => {
      if (value === undefined || value === '' || value === null) {
        return undefined;
      }
      const parsed = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }),
  company: z.string().max(255).optional(),
  carPlate: z.string().max(20).optional(),
  tags: z.string().max(1000).optional(),
  additionalEmail: z
    .email('Invalid additional email format')
    .max(255)
    .optional(),
  userId: z.union([z.uuid('Invalid user ID format'), z.null()]).optional(),
  organizationId: z.uuid('Invalid organization ID format').optional(),
  type: z.enum(['employee', 'contractor']),
  preferredLanguage: z.string().min(2).max(10).optional(),
});

// Create schema - requires all fields
export const createPersonSchema = personBaseSchema;
export type CreatePersonInput = z.infer<typeof createPersonSchema>;

// Update schema - all fields optional, requires id
export const updatePersonSchema = personBaseSchema.partial().extend({
  id: z.uuid('Invalid person ID format'),
});
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;

// Get schema - requires id only
export const getPersonSchema = z.object({
  id: z.uuid('Invalid person ID format'),
});
export type GetPersonInput = z.infer<typeof getPersonSchema>;

// Delete schema - requires id only
export const deletePersonSchema = z.object({
  id: z.uuid('Invalid person ID format'),
});
export type DeletePersonInput = z.infer<typeof deletePersonSchema>;

// List schema - cursor pagination and filtering
export const listPersonsSchema = createCursorPaginationSchema(
  ['firstName', 'lastName', 'email', 'createdAt'],
  'createdAt',
).extend({
  /** Filter to persons belonging to this organization. */
  organizationId: z.uuid().optional(),
  /**
   * Narrow to the persons of this unit and every unit below it (UI-003). An
   * optional filter: the search covers the whole organigram without it.
   */
  organizationUnitId: z.uuid().optional(),
  filters: z
    .object({
      firstName: z.string().trim().min(1).optional(),
      lastName: z.string().trim().min(1).optional(),
      types: z.array(z.enum(['employee', 'contractor'])).optional(),
    })
    .optional(),
});
export type ListPersonsInput = z.infer<typeof listPersonsSchema>;

// Find by email schema
export const findByEmailPersonSchema = z.object({
  email: z.email('Invalid email format'),
});
export type FindByEmailPersonInput = z.infer<typeof findByEmailPersonSchema>;

// Find by email schema
export const bulkFindByEmailPersonSchema = z.object({
  emails: z.array(z.string('Invalid email format')),
});
export type BulkFindByEmailPersonInput = z.infer<
  typeof bulkFindByEmailPersonSchema
>;

// Get by user ID schema
export const getByUserIdPersonSchema = z.object({
  userId: z.uuid('Invalid user ID format'),
});
export type GetByUserIdPersonInput = z.infer<typeof getByUserIdPersonSchema>;
